var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button, Modal, ModalFooter, MessageBanner, Checkbox, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { RecekTextField, RecekOnixItemPicker, RecekSelection } from "../inputs";
import { useThemaOptions } from "../../hooks/title/useThemaOptions";
import { THEMA_SCHEME_TO_CODETABLE } from "../../utils/onixTables";
import { isThemaQualifierScheme } from "../../utils/themaSubjectRules";
import { KEYWORDS_SCHEME, canBeMainSubject, } from "../../utils/mainSubjectRules";
import css from "./FormModal.module.css";
var THEMA_ROW = 0;
var SubjectFormModal = function (_a) {
    var _b, _c, _d;
    var open = _a.open, isEditing = _a.isEditing, draft = _a.draft, onDraftChange = _a.onDraftChange, onSave = _a.onSave, onClose = _a.onClose, subjectSchemeItems = _a.subjectSchemeItems, subjectSchemeLoading = _a.subjectSchemeLoading, subjectSchemeStatus = _a.subjectSchemeStatus, hasThemaSubjectCategory = _a.hasThemaSubjectCategory;
    var formatMessage = useIntl().formatMessage;
    var _e = useState(null), error = _e[0], setError = _e[1];
    var _f = useThemaOptions(), themaOptionsMap = _f.themaOptionsMap, onThemaFilter = _f.onThemaFilter, clearThemaRow = _f.clearThemaRow;
    var clearThemaRowRef = useRef(clearThemaRow);
    clearThemaRowRef.current = clearThemaRow;
    useEffect(function () {
        if (open) {
            setError(null);
            if (!isEditing) {
                clearThemaRowRef.current(THEMA_ROW);
            }
        }
    }, [open, isEditing]);
    var schemeValue = (_c = (_b = draft.scheme) === null || _b === void 0 ? void 0 : _b.value) !== null && _c !== void 0 ? _c : "";
    var isThema = schemeValue in THEMA_SCHEME_TO_CODETABLE;
    var isKeywords = schemeValue === KEYWORDS_SCHEME;
    // Keywords (20) and Thema qualifiers (94–99) are never the main subject (issue #76113).
    var mainSubjectAllowed = canBeMainSubject(schemeValue);
    var isUDC = schemeValue === "09";
    var codeRequired = isThema || isUDC;
    var textRequired = isKeywords;
    var handleSchemeChange = function (item) {
        onDraftChange(function (prev) { return (__assign(__assign({}, prev), { scheme: item, 
            // SubjectCode holds the term code, never the scheme code — start empty on scheme change.
            code: "", text: "", mainSubject: canBeMainSubject(item === null || item === void 0 ? void 0 : item.value) && !!prev.mainSubject })); });
        clearThemaRow(THEMA_ROW);
    };
    var handleThemaCodeChange = function (val) {
        var _a, _b;
        var opts = (_a = themaOptionsMap[THEMA_ROW]) !== null && _a !== void 0 ? _a : [];
        var chosen = opts.find(function (o) { return o.value === val; });
        var text = ((_b = chosen === null || chosen === void 0 ? void 0 : chosen.label) === null || _b === void 0 ? void 0 : _b.split("—").slice(1).join("—").trim()) ||
            (chosen === null || chosen === void 0 ? void 0 : chosen.label) ||
            val;
        onDraftChange(function (prev) { return (__assign(__assign({}, prev), { code: val, text: text })); });
    };
    var handleSave = function () {
        var _a, _b, _c;
        if (!((_a = draft.scheme) === null || _a === void 0 ? void 0 : _a.value)) {
            setError(formatMessage({ id: "ui-br-agency.title.form.error.required" }));
            return;
        }
        if (codeRequired && !((_b = draft.code) === null || _b === void 0 ? void 0 : _b.trim())) {
            setError(formatMessage({ id: "ui-br-agency.title.form.error.required" }));
            return;
        }
        if (textRequired && !((_c = draft.text) === null || _c === void 0 ? void 0 : _c.trim())) {
            setError(formatMessage({ id: "ui-br-agency.title.form.error.required" }));
            return;
        }
        // A Thema qualifier (94–99) requires a Thema category (93) term on the title.
        if (isThemaQualifierScheme(schemeValue) && !hasThemaSubjectCategory) {
            setError(formatMessage({
                id: "ui-br-agency.title.validation.subjects.themaQualifierWithoutCategory",
            }));
            return;
        }
        setError(null);
        onSave();
    };
    var modalLabel = isEditing
        ? formatMessage({ id: "ui-br-agency.title.subjects.form.edit.title" })
        : formatMessage({ id: "ui-br-agency.title.subjects.form.add.title" });
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSave },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.save" })),
        React.createElement(Button, { buttonStyle: "default", onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-br-agency.button.cancel" }))));
    return (React.createElement(Modal, { open: open, onClose: onClose, label: modalLabel, footer: footer, size: "large", dismissible: true },
        error && (React.createElement(MessageBanner, { type: "error", className: css.errorBanner }, error)),
        React.createElement(Row, null,
            React.createElement(Col, { xs: 1 }, mainSubjectAllowed && (React.createElement(Checkbox, { label: formatMessage({
                    id: "ui-br-agency.title.subject.main.label",
                }), checked: draft.mainSubject || false, onChange: function (e) {
                    return onDraftChange(function (prev) { return (__assign(__assign({}, prev), { mainSubject: e.target.checked })); });
                } }))),
            React.createElement(Col, { xs: 4 },
                React.createElement(RecekOnixItemPicker, { labelId: "ui-br-agency.title.subject.scheme.label", dataOptions: subjectSchemeItems, value: draft.scheme, onChange: handleSchemeChange, required: true, loading: subjectSchemeLoading, codetableStatus: subjectSchemeStatus })),
            React.createElement(Col, { xs: 3 }, isThema ? (React.createElement(RecekSelection, { labelId: "ui-br-agency.title.subject.code.label", dataOptions: (_d = themaOptionsMap[THEMA_ROW]) !== null && _d !== void 0 ? _d : [], asyncFilter: true, onFilter: onThemaFilter(THEMA_ROW, schemeValue), value: draft.code || "", onChange: handleThemaCodeChange, required: codeRequired })) : isKeywords ? null : ( // Keywords (scheme 20) carry only text, never a SubjectCode.
            React.createElement(RecekTextField, { labelId: "ui-br-agency.title.subject.code.label", value: draft.code || "", onChange: function (e) {
                    return onDraftChange(function (prev) { return (__assign(__assign({}, prev), { code: e.target.value })); });
                }, required: codeRequired }))),
            React.createElement(Col, { xs: 4 },
                React.createElement(RecekTextField, { labelId: "ui-br-agency.title.subject.text.label", value: draft.text || "", onChange: function (e) {
                        return onDraftChange(function (prev) { return (__assign(__assign({}, prev), { text: e.target.value })); });
                    }, disabled: isThema || isUDC, required: textRequired })))));
};
export { SubjectFormModal };
